<!--
 Import statment for AutoComplete
 import AutoComplete from '@/components/Core/Form/AutoComplete.vue'
-->
<template>
  <FloatLabel :variant="variant">
    <AutoComplete
      ref="autocompleteRef"
      :modelValue="selectedItem"
      :suggestions="filteredOptions"
      :completeOnFocus="completeOnFocus"
      :delay="delay"
      :disabled="disabled"
      :dropdown="dropdown"
      :loading="props.loading || loading"
      :placeholder="label ? null : placeholder"
      :showClear="showClear"
      :size="size"
      :optionLabel="optionLabel"
      :tabindex="-1"
      @complete="search"
      @hide="removeFocusOnceOptionIsSelected"
      @option-select="itemSelected"
      @change="handleClearingInput"
      @focus="emit('focus'), inputFocused = true"
      @blur="emit('blur'), inputFocused = false"
      :pt="{
        root: {
          class: [
            // Size
            'w-full'
          ]
        },

        pcInputText: {
          root: ({ context }) => ({
            class: [
              // Size
              'w-full',

              // Color and Background
              'focus:border-iris-600',
              'focus:ring-iris-300/40 dark:focus:ring-iris-400/40',
              'placeholder:text-charcoal-700 dark:placeholder:text-charcoal-400',

              { 'focus:ring-2': !minimal },

              // Minimal State
              { 'border-b shadow-none rounded-none border-t-0 border-l-0 border-r-0 focus:ring-0 bg-transparent' : minimal },


              // Disabled State
              { 'cursor-not-allowed pointer-events-auto opacity-70': context.disabled },
              { 'bg-transparent' : context.disabled && minimal },
              { ' bg-white dark:bg-charcoal-900' : context.disabled && !minimal },

              // Setting Changed and Error border and ring colors
              setInputStateBorderAndRingColor(),

              // Misc
              'transition-none'
            ]
          }),
        },

        option: ({ context }) => ({
          class: [
            // Color and Background
            { 'bg-iris-600 text-white': context.focused },
            { 'bg-iris-500/85 text-white dark:bg-iris-500/40 dark:text-white': context.selected },

            // Misc
            'truncate',
            'group'
          ]
        }),

        overlay: {
          class: 'border-charcoal-200 dark:border-charcoal-800'
        }
      }"
    >
      <template #chip>
        <slot name="input"/>
      </template>

      <template #option="{ option }">
        <!-- If needing a unique slot layout, use the 'options' slot -->
        <template v-if="slots.options">
          <slot name="options" :option="option"/>
        </template>

        <!-- Default Option layout -->
        <template v-else>
          <div class="w-full flex justify-between">
            <div>{{ option[optionLabel] }}</div>
            <!-- If adding a trailing icon to the option in option list -->
            <div v-if="option.trailingIcon?.icon"
              :class="[
                'self-center',
                (
                  option.trailingIcon.color ||
                  'text-green-700 group-hover:text-green-300 dark:text-green-600 group-hover:dark:text-green-300'
                )
              ]">
              <Icon
                :icon="option.trailingIcon.icon"
                class="size-5"
                aria-hidden="true"
                :content="option.trailingIcon.tippy"
                v-tippy="{ onShow: () => option.trailingIcon.tippy ? true : false }"
              />
            </div>
          </div>
        </template>
      </template>

      <!-- To change the default 'No results found' message when no results were found -->
      <template #empty>
        <template v-if="slots.emptyState">
          <slot name="emptyState"/>
        </template>
      </template>

      <!-- Can be used under the options list or for something like Action Button on Case Details > Tap Request for example -->
      <template #footer>
        <slot name="footer"/>
      </template>
    </AutoComplete>

    <!-- Float Label label -->
    <label v-if="label">{{ label }}</label>
  </FloatLabel>
</template>

<script setup>
import AutoComplete from 'primevue/autocomplete';
import { ref, computed, useSlots, watch } from 'vue'
import FloatLabel from 'primevue/floatlabel';
import { Icon } from '@iconify/vue'

const props = defineProps({
  // If autocomplete is an API call
  apiSearch: {
    type: Function
  },
  // Setting the Yellow border/ring around the input
  changed: {
    type: Boolean
  },
  // Whether to run a query when input receives focus, aka as soon as it is focused dropdown opens
  completeOnFocus: {
    type: Boolean
  },
  // Delay between keystrokes to wait before sending a query
  delay: {
    type: Number,
    default: 300
  },
  disabled: {
    type: Boolean
  },
  // To display the dropdown button to instantiate the dropdown opening
  dropdown: {
    type: Boolean
  },
  // Setting the Red border/ring around the input
  error: {
    type: Boolean
  },
  // Used in conjunction with variant prop for Float Label
  label: {
    type: String
  },
  // Component Loading state outside of API search which also has a search loading state
  loading: {
    type: Boolean,
    default: false
  },
  // For a border bottom instead of entire border
  minimal: {
    type: Boolean
  },
  // Array of options
  options: {
    type: Array,
    default: []
  },
  // Key used in option object to display in options and input
  optionLabel: {
    type: String,
    default: 'label'
  },
  placeholder: {
    type: String
  },
  // To show clear button if input has value
  showClear: {
    type: Boolean
  },
  // Controls the padding and text size of the input
  size: {
    type: String,
    validator: (val) => ['small', 'large'].includes(val),
    default: null
  },
  // Used for the Float Label
  variant: {
    type: String,
    validator: (val) => ['in', 'on', 'over'].includes(val),
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'input', 'focus', 'blur'])
const slots = useSlots()

const autocompleteRef = ref(null)
const filteredOptions = ref(null)
const initialOptions = ref([])
const inputFocused = ref(false)
const loading = ref(false)
const options = computed(() => props.options)
const selectedItem = defineModel({ default: null })

/**
 * If a selectedItem exists on page load, the search function short-circuits and returns
 * without populating the filteredOptions array used by the AutoComplete component.
 *
 * This watcher monitors incoming options and, if options are available and filteredOptions
 * hasn’t been set yet (or is still empty, e.g. options that load asynchronously after mount),
 * it assigns filteredOptions to props.options.
*/
watch(() => props.options, (options) => {
  if (options && !filteredOptions.value?.length) {
    filteredOptions.value = options
  }
}, {immediate: true})

const search = async ({ query }) => {
  /**
   * Short circuit when the selected value and query match.
   * This prevents running the fetch again keeping options same as previously searched
  */
  if (selectedItem.value && (typeof props.apiSearch === 'function' && query === selectedItem.value[props.optionLabel])) {
    return
  }

  // Setting the temp options variable to the options passed in on load
  initialOptions.value = options.value

  if (typeof props.apiSearch === 'function' && query.length > 1) {
    try {
      loading.value = true
      initialOptions.value = await props.apiSearch(initialOptions.value, query)
    }
    finally {
      loading.value = false
    }
  }

  /**
   * We need the setTimeout to avoid a flicker when no results are found when resetting the options
   * to the default options list
  */
  setTimeout(() => {
    filteredOptions.value = query === ''
      ? initialOptions.value
      : initialOptions.value.filter((option) =>
          option[props.optionLabel]
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, ''))
      )
  }, 70)
}

function setInputStateBorderAndRingColor() {
  if (!inputFocused.value) {
    // If minimal input styling
    if (props.minimal) {
      if (props.error) {
        return 'border-red-500 ring-red-500/40'
      }
      else if (props.changed) {
        return 'border-yellow-500 ring-yellow-500/40'
      }
    }
    // If input has errors styling
    else if (props.error) {
      return 'border-red-500 ring-2 ring-red-500/40'
    }
    // If input is changed styling
    else if (props.changed) {
      return 'border-yellow-500 ring-2 ring-yellow-500/40'
    }

    // Default border for when input is not focused
    return 'border-charcoal-200 dark:border-charcoal-800'
  }
}

/**
 * This is a workaround to allow users to be able to make a selection then click back to open options
 * dropdown again.
 *
 * By default, once a user makes a selection, the input retains focus not allowing users to click and open
 * options again unless they click outside to remove focus and then click back in to open the dropdown
*/
function removeFocusOnceOptionIsSelected() {
  autocompleteRef.value?.$el?.querySelector('input').blur()
}

// When a selection is made by selecting an option in the list
function itemSelected({ value }) {
  emit('update:modelValue', value)
}

const handleClearingInput = ({ value }) => {
  // Input has been cleared via the 'X' icon or manually
  if (value === null || value === '') {
    emit('update:modelValue', null)
  }

    // Emit input to component instantiation to be used in instances like determining if anything is typed into AutoComplete
    emit('input', value)
}
</script>